import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { Card, Tag, Tooltip, Button } from 'antd';
import { Wrench, Play, CheckCircle, Settings } from 'lucide-react';
import { DeleteOutlined, WarningOutlined } from '@ant-design/icons';

interface ToolNodeData {
  label: string;
  tool: string;
  description?: string;
  config?: Record<string, any>;
  isExecuting?: boolean;
  isCompleted?: boolean;
  onDelete?: () => void;
  isUnlinked?: boolean;
}

const ToolNode: React.FC<NodeProps<ToolNodeData>> = ({ data, selected }) => {
  const { label, tool, description, config, isExecuting, isCompleted, onDelete, isUnlinked } = data;

  const getCardStyle = () => {
    if (isExecuting) {
      return {
        borderColor: '#fa8c16',
        boxShadow: '0 0 10px rgba(250, 140, 22, 0.5)',
        animation: 'pulse 1.5s ease-in-out infinite'
      };
    }
    if (isCompleted) {
      return {
        borderColor: '#52c41a',
        backgroundColor: '#f6ffed'
      };
    }
    if (selected) {
      return {
        borderColor: '#722ed1',
        boxShadow: '0 0 0 2px rgba(114, 46, 209, 0.2)'
      };
    }
    return {
      borderColor: '#d9d9d9'
    };
  };

  const getToolIcon = () => {
    if (isExecuting) return <Play className="h-4 w-4 text-orange-600" />;
    if (isCompleted) return <CheckCircle className="h-4 w-4 text-green-600" />;
    return <Wrench className="h-4 w-4 text-orange-600" />;
  };

  const getToolColor = () => {
    switch (tool) {
      case 'enhance_query': return 'blue';
      case 'hybrid_search': return 'green';
      case 'vector_search': return 'cyan';
      case 'evaluate_response': return 'purple';
      case 'context_selection': return 'geekblue';
      default: return 'orange';
    }
  };

  return (
    <>
      <Handle
        type="target"
        position={Position.Top}
        style={{ background: '#fa8c16' }}
      />
      
      <Card
        size="small"
        style={{
          width: 220,
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          position: 'relative',
          ...getCardStyle()
        }}
        className="tool-node"
      >
        {/* Caution Icon for Unlinked Nodes */}
        {isUnlinked && (
          <Tooltip title="This node is not connected to any other nodes">
            <WarningOutlined
              style={{
                position: 'absolute',
                top: '4px',
                left: '4px',
                color: '#faad14',
                fontSize: '14px',
                zIndex: 10,
                backgroundColor: 'white',
                borderRadius: '50%',
                padding: '2px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
              }}
            />
          </Tooltip>
        )}
        
        {/* Delete Button */}
        {onDelete && (
          <Button
            type="text"
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            style={{
              position: 'absolute',
              top: '4px',
              right: '4px',
              width: '20px',
              height: '20px',
              fontSize: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              opacity: 0.7,
              transition: 'opacity 0.2s'
            }}
            className="node-delete-btn"
          />
        )}
        <div className="flex items-start space-x-2">
          <div className="flex-shrink-0">
            <div className={`p-2 rounded-lg ${isExecuting ? 'bg-orange-100' : isCompleted ? 'bg-green-100' : 'bg-orange-100'}`}>
              {getToolIcon()}
            </div>
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-gray-900 truncate">
                {label}
              </h4>
              {config && Object.keys(config).length > 0 && (
                <Tooltip title="Tool Configuration">
                  <Settings className="h-3 w-3 text-gray-400" />
                </Tooltip>
              )}
            </div>
            
            <div className="mt-1">
              <Tag color={getToolColor()}>
                {tool}
              </Tag>
            </div>
            
            {description && (
              <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                {description}
              </p>
            )}
            
            {config && Object.keys(config).length > 0 && (
              <div className="mt-2 text-xs text-gray-600">
                <div className="space-y-1">
                  {Object.entries(config).slice(0, 2).map(([key, value]) => (
                    <div key={key} className="flex justify-between">
                      <span className="truncate">{key}:</span>
                      <span className="ml-1 font-mono">{String(value)}</span>
                    </div>
                  ))}
                  {Object.keys(config).length > 2 && (
                    <div className="text-center text-gray-400">
                      +{Object.keys(config).length - 2} more
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {isExecuting && (
              <div className="mt-2">
                <div className="flex items-center space-x-1">
                  <div className="animate-spin h-3 w-3 border-2 border-orange-600 border-t-transparent rounded-full" />
                  <span className="text-xs text-orange-600">Executing...</span>
                </div>
              </div>
            )}
            
            {isCompleted && (
              <div className="mt-2">
                <div className="flex items-center space-x-1">
                  <div className="h-3 w-3 bg-green-600 rounded-full" />
                  <span className="text-xs text-green-600">Completed</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>
      
      <Handle
        type="source"
        position={Position.Bottom}
        style={{ background: '#fa8c16' }}
      />
      
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
        
        .tool-node:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
        }

        .tool-node .node-delete-btn {
          opacity: 0;
          transition: opacity 0.2s;
        }

        .tool-node:hover .node-delete-btn {
          opacity: 1;
        }

        .node-delete-btn:hover {
          background-color: #ff4d4f !important;
          color: white !important;
        }
        
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </>
  );
};

export default ToolNode;